import { FilterCategoryNav } from "@app/components/shared/filter_panel/FilterCategoryNav";
import { FilterFooter } from "@app/components/shared/filter_panel/FilterFooter";
import { FilterOptionCheckboxList } from "@app/components/shared/filter_panel/FilterOptionCheckboxList";
import { FilterSelectionSummary } from "@app/components/shared/filter_panel/FilterSelectionSummary";
import { AutomationsFilterOptionIcon } from "@app/components/workspace/analytics/automations/AutomationsFilterOptionIcon";
import { POOL_OPTIONS } from "@app/components/workspace/analytics/automations/trigger_pool_options";
import type {
  AutomationsFilter,
  AutomationsFilterCategory,
  AutomationsFilterOption,
} from "@app/components/workspace/analytics/automationsFilter";
import {
  AUTOMATIONS_FILTER_CATEGORIES,
  AUTOMATIONS_FILTER_CATEGORY_LABEL,
  automationsFilterSelectionCount,
  toAutomationsScopeFilter,
} from "@app/components/workspace/analytics/automationsFilter";
import { useAutomationsFilter } from "@app/components/workspace/analytics/useAutomationsFilter";
import { useConsumptionFacets } from "@app/hooks/useConsumptionFacets";
import type { ConsumptionPeriodSelection } from "@app/lib/analytics/consumption_period";
import type { ConsumptionScopeDimension } from "@app/types/api/analytics/consumption";
import type { LightWorkspaceType } from "@app/types/user";
import {
  Button,
  FilterFunnel01,
  NavigationListLabel,
  PopoverContent,
  PopoverRoot,
  PopoverTrigger,
  SearchInput,
} from "@dust-tt/sparkle";
import type { MessageDescriptor } from "@lingui/core";
import { msg } from "@lingui/core/macro";
import { Trans, useLingui } from "@lingui/react/macro";
import { useMemo, useState } from "react";

// The panel only shows agents and members, so it skips the other dimensions
// rather than paying for their Elasticsearch sweeps and catalogs.
const AUTOMATIONS_FACET_DIMENSIONS: ConsumptionScopeDimension[] = [
  "agent",
  "user",
];

const SEARCH_PLACEHOLDER: Record<AutomationsFilterCategory, MessageDescriptor> =
  {
    agent: msg`Search agents`,
    member: msg`Search members`,
    type: msg`Search type`,
    pool: msg`Search pool`,
  };

interface AutomationsFilterPanelProps {
  owner: LightWorkspaceType;
  period: ConsumptionPeriodSelection;
  filter: AutomationsFilter;
  onFilterChange: (next: AutomationsFilter) => void;
  categories?: readonly AutomationsFilterCategory[];
  agentOptions?: { agentId: string; name: string; pictureUrl: string | null }[];
}

export function AutomationsFilterPanel({
  owner,
  period,
  filter,
  onFilterChange,
  categories = AUTOMATIONS_FILTER_CATEGORIES,
  agentOptions,
}: AutomationsFilterPanelProps) {
  const { t } = useLingui();
  const [isOpen, setIsOpen] = useState(false);
  const {
    draftFilter,
    setDraftFilter,
    clearAllCategories,
    clearCategory,
    toggleOption,
    removeOption,
    selectAllFiltered,
  } = useAutomationsFilter(filter);
  const [activeCategory, setActiveCategory] =
    useState<AutomationsFilterCategory>(categories[0]);
  const [searchText, setSearchText] = useState("");
  const [contentScrollContainer, setContentScrollContainer] =
    useState<HTMLDivElement | null>(null);

  const draftScopeFilter = useMemo(
    () => toAutomationsScopeFilter(draftFilter),
    [draftFilter]
  );
  const {
    options: facetOptions,
    isFacetsLoading,
    isFacetsError,
    isFacetsValidating,
  } = useConsumptionFacets({
    workspaceId: owner.sId,
    period,
    filter: draftScopeFilter,
    scope: "automations",
    dimensions: AUTOMATIONS_FACET_DIMENSIONS,
    disabled: !isOpen || agentOptions !== undefined,
  });

  const categoryOptions = useMemo<
    Record<AutomationsFilterCategory, AutomationsFilterOption[]>
  >(
    () => ({
      agent: agentOptions
        ? agentOptions.map((option) => ({
            id: option.agentId,
            name: option.name,
            disabled: false,
            image: option.pictureUrl,
            category: "agent",
          }))
        : facetOptions.agent.map((option) => ({
            id: option.id,
            name: option.name,
            disabled: option.disabled,
            image: option.image,
            category: "agent",
          })),
      member: facetOptions.member.map((option) => ({
        id: option.id,
        name: option.name,
        disabled: option.disabled,
        image: option.image,
        category: "member",
      })),
      type: [
        {
          id: "schedule",
          name: t`Schedule`,
          disabled: false,
          category: "type",
        },
        { id: "webhook", name: t`Webhook`, disabled: false, category: "type" },
      ],
      pool: POOL_OPTIONS.map(({ value, label }) => ({
        id: value,
        name: t(label),
        disabled: false,
        category: "pool",
      })),
    }),
    [agentOptions, facetOptions, t]
  );
  const categoryLabels = useMemo<Record<AutomationsFilterCategory, string>>(
    () => ({
      agent: t(AUTOMATIONS_FILTER_CATEGORY_LABEL.agent),
      member: t(AUTOMATIONS_FILTER_CATEGORY_LABEL.member),
      type: t(AUTOMATIONS_FILTER_CATEGORY_LABEL.type),
      pool: t(AUTOMATIONS_FILTER_CATEGORY_LABEL.pool),
    }),
    [t]
  );

  const isFacetBackedCategory =
    activeCategory !== "type" && activeCategory !== "pool";
  const isOptionsLoading = isFacetBackedCategory && isFacetsLoading;

  const activeOptions = categoryOptions[activeCategory];
  const filteredOptions = useMemo(() => {
    const search = searchText.trim().toLowerCase();
    return search
      ? activeOptions.filter((option) =>
          option.name.toLowerCase().includes(search)
        )
      : activeOptions;
  }, [activeOptions, searchText]);

  const selectedIdsForActiveCategory = useMemo(
    () =>
      new Set((draftFilter[activeCategory] ?? []).map((option) => option.id)),
    [draftFilter, activeCategory]
  );
  const unselectedEnabledOptions = filteredOptions.filter(
    (option) => !option.disabled && !selectedIdsForActiveCategory.has(option.id)
  );
  const appliedSelectionCount = automationsFilterSelectionCount(
    filter,
    categories
  );
  const categoriesWithSelection = useMemo(
    () =>
      categories.filter((category) => (draftFilter[category]?.length ?? 0) > 0),
    [categories, draftFilter]
  );
  const categorySelectionCounts = useMemo(() => {
    const counts: Partial<Record<AutomationsFilterCategory, number>> = {};
    for (const category of categories) {
      counts[category] = draftFilter[category]?.length ?? 0;
    }
    return counts;
  }, [categories, draftFilter]);

  const handleOpenChange = (open: boolean) => {
    setIsOpen(open);
    if (open) {
      setDraftFilter(filter);
      setSearchText("");
    }
  };

  const resetContentScroll = () => {
    if (contentScrollContainer) {
      contentScrollContainer.scrollTop = 0;
    }
  };

  const handleCategoryChange = (category: AutomationsFilterCategory) => {
    setActiveCategory(category);
    setSearchText("");
    resetContentScroll();
  };

  const handleSearchChange = (search: string) => {
    setSearchText(search);
    resetContentScroll();
  };

  const activeCategorySelectionCount = draftFilter[activeCategory]?.length ?? 0;

  return (
    <PopoverRoot open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          icon={FilterFunnel01}
          label={t`Filters`}
          size="sm"
          variant="outline"
          isCounter={appliedSelectionCount > 0}
          counterValue={String(appliedSelectionCount)}
        />
      </PopoverTrigger>
      <PopoverContent fullWidth align="end" className="w-auto rounded-2xl p-0">
        <div className="flex h-96 flex-row divide-x divide-border">
          <FilterCategoryNav
            categories={categories}
            categoryLabels={categoryLabels}
            selectionCounts={categorySelectionCounts}
            activeCategory={activeCategory}
            onCategoryChange={handleCategoryChange}
          />
          <div className="flex h-full w-72 flex-col gap-2 p-2">
            <NavigationListLabel
              label={categoryLabels[activeCategory]}
              className="bg-transparent pt-1.5 pb-0 font-medium"
              action={
                <Button
                  label={t`Clear`}
                  size="xmini"
                  variant="ghost-secondary"
                  onClick={() => clearCategory(activeCategory)}
                  disabled={activeCategorySelectionCount === 0}
                  className={
                    activeCategorySelectionCount === 0 ? "invisible" : undefined
                  }
                />
              }
            />
            <SearchInput
              name="automations-filter-search"
              value={searchText}
              onChange={handleSearchChange}
              placeholder={t(SEARCH_PLACEHOLDER[activeCategory])}
            />
            <div
              ref={setContentScrollContainer}
              className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto"
            >
              {isFacetBackedCategory && isFacetsError ? (
                <div className="flex h-24 items-center justify-center text-sm text-muted-foreground">
                  <Trans>Failed to load filters.</Trans>
                </div>
              ) : (
                <FilterOptionCheckboxList
                  key={`${isOpen}|${activeCategory}|${searchText}`}
                  idPrefix={`automations-filter-option-${activeCategory}`}
                  options={filteredOptions}
                  selectedIds={selectedIdsForActiveCategory}
                  onToggleOption={(option) =>
                    toggleOption(activeCategory, option)
                  }
                  onSelectAll={() =>
                    selectAllFiltered(activeCategory, unselectedEnabledOptions)
                  }
                  selectAllLabel={t`Select all`}
                  hasSelectableOptions={unselectedEnabledOptions.length > 0}
                  renderIcon={(option) => (
                    <AutomationsFilterOptionIcon option={option} />
                  )}
                  status={
                    isOptionsLoading
                      ? "loading"
                      : isFacetBackedCategory && isFacetsValidating
                        ? "updating"
                        : "idle"
                  }
                  scrollContainer={contentScrollContainer}
                />
              )}
            </div>
          </div>
          <FilterSelectionSummary<
            AutomationsFilterCategory,
            AutomationsFilterOption
          >
            categoriesWithSelection={categoriesWithSelection}
            categoryLabels={categoryLabels}
            filter={draftFilter}
            onClearCategory={clearCategory}
            onRemoveOption={removeOption}
            renderIcon={(option) => (
              <AutomationsFilterOptionIcon option={option} />
            )}
          />
        </div>
        <FilterFooter
          onClearAll={clearAllCategories}
          onCancel={() => setIsOpen(false)}
          onApply={() => {
            onFilterChange(draftFilter);
            setIsOpen(false);
          }}
        />
      </PopoverContent>
    </PopoverRoot>
  );
}
